import type { AnyAction } from "redux";
import type { Epic } from "redux-observable";

import {
	type StackPackStateChangeEvent,
	type StatusUpdateRequest,
	StatusUpdateType,
	Type,
	WebSocketStreamType,
	isStackPackStateChangeEvent,
} from "~/models/dto";
import { Logger } from "~/tools/logger";
import { catchError, filter, map, switchMap, takeUntil } from "~/tools/rxjs";
import { createEpicErrorHandler } from "~/tools/rxjs/create-epic-error-handler";

import { streamApiService } from "../../../../backend";
import { ofType, typedStream } from "../../../../generic/redux/TypedRedux";
import { invalidateStackPackNodes } from "../../../../queries";
import { selectWebUiConfig } from "../../config/ConfigSelectors";
import {
	type StartStackPackStatusUpdateStream,
	type UpdateConfigurationStatus,
	updateConfigurationStatus,
} from "../../pages/stackPacks/details/StackPacksDetailsActions";
import {
	STACK_PACKS_START_STATUS_UPDATE_STREAM,
	STACK_PACKS_STOP_STATUS_UPDATE_STREAM,
} from "../../pages/stackPacks/details/StackPacksDetailsActionTypes";
import type { EpicDependencies } from "../../ReduxStore";
import type { RootState } from "../../RootState";

const logger = Logger.namespace("StackPacksStatusUpdateStreamEpic");

const startStreamRequest: StatusUpdateRequest = {
	_type: Type.StatusUpdateRequest,
	updateType: StatusUpdateType.StackPackUpdate,
};

export const StatusUpdateStreamEpic: Epic<AnyAction, UpdateConfigurationStatus, RootState, EpicDependencies> = (
	actions,
	_states,
	dependencies,
) =>
	actions.pipe(
		typedStream<StartStackPackStatusUpdateStream>(STACK_PACKS_START_STATUS_UPDATE_STREAM),
		switchMap(() => {
			const config = selectWebUiConfig(_states.value);
			return streamApiService.startStream(WebSocketStreamType.StatusUpdateStream, startStreamRequest, config).pipe(
				filter<StackPackStateChangeEvent>(isStackPackStateChangeEvent),
				map((message) => {
					void invalidateStackPackNodes(dependencies.queryClient);
					return updateConfigurationStatus(message);
				}),
				catchError(createEpicErrorHandler(logger)),
				takeUntil(actions.pipe(ofType(STACK_PACKS_STOP_STATUS_UPDATE_STREAM))),
			);
		}),
	);
